import * as React from "react";
import Form from "../commons/components/form/Form";
import EmailInput from "../commons/components/form/input/EmailInput";
import TextInput from "../commons/components/form/input/TextInput";
import PasswordInput from "../commons/components/form/input/PasswordInput";
import InputState from "../commons/model/form/InputState";
import Field from "../commons/model/form/Field";
import { getPayload } from "../commons/utils/form-utils";
import { minLength, required } from "../commons/model/form/Validators";
import "whatwg-fetch";
import ConsentCheckboxInput from "../legal/ConsentCheckboxInput";
import LegalConsentProps from "../legal/LegalConsentProps";
import { sendToServerForValidation } from "./utils";

interface PersonalDetailsFormProps extends LegalConsentProps {
  validateEndpoint?: string;
  submitLabel: string;
  onSubmitSucceeded: (payload: any, form: { [key: string]: InputState }) => void;
  errorMsg?: string;
  fields?: Field[];
}

export default class PersonalDetailsForm extends React.Component<PersonalDetailsFormProps> {
  private readonly email;

  constructor(props: PersonalDetailsFormProps) {
    super(props);
    this.submit = this.submit.bind(this);
    this.email = new URLSearchParams(document.location.search).get("invitedEmail");
  }

  render() {
    return (
      <Form
        titleLabel={i18n["Form.GenericRegistration.PersonalInformation.Title"]}
        submitLabel={this.props.submitLabel}
        submit={this.submit}
        generalErrorMsg={this.props.errorMsg}
      >
        {this.props.fields ? (
          Form.getFieldComponents(this.props.fields)
        ) : (
          <>
            <TextInput
              name="firstName"
              label={i18n["Global.FirstName"]}
              placeholder={i18n["Form.GenericRegistration.Placeholder.FirstName"]}
              validators={[required()]}
            />
            <TextInput
              name="lastName"
              label={i18n["Global.LastName"]}
              placeholder={i18n["Form.GenericRegistration.Placeholder.LastName"]}
              validators={[required()]}
            />
          </>
        )}
        <EmailInput
          name="email"
          label={i18n["Global.EmailAddress"]}
          initialValue={this.email}
          placeholder={i18n["Form.GenericRegistration.Placeholder.Email"]}
          validators={[required()]}
          readOnly={this.email !== null}
        />
        <PasswordInput
          name="password"
          label={i18n["Form.GenericRegistration.Title.Password"]}
          detail={i18n["Form.PasswordDetail"]}
          placeholder={i18n["Global.PasswordPrompt"]}
          validators={[required(), minLength(i18n["Form.Error.Password.MinSize"], 8)]}
        />
        {this.props.legalConsent && (
          <ConsentCheckboxInput
            name="legalDocuments"
            legalResources={this.props.legalConsent.legalResources}
            practiceToken={this.props.legalConsent.practiceToken}
            country={this.props.legalConsent.country}
            onError={this.props.legalConsent.onError}
          />
        )}
      </Form>
    );
  }

  private submit(form: { [key: string]: InputState }): Promise<void> {
    const payload = {
      account: getPayload(form, ["firstName", "lastName", "email", "password"]),
    };

    if (this.props.validateEndpoint) {
      return sendToServerForValidation(payload, this.props.validateEndpoint)
        .then(() => this.props.onSubmitSucceeded(payload, form))
        .catch((response) => {
          if (response.status == 400) {
            response.json().then((json) => {
              if (json["status"] == "ERROR_EMAIL_MISMATCH") {
                form["email"].status.setError(
                  i18n["Form.GenericRegistration.Error.EmailDifferentFromInvitation"]
                );
              } else if (json["status"] == "ERROR_EXISTING_EMAIL") {
                form["email"].status.setError(
                  i18n["Form.GenericRegistration.Error.EmailAlreadyInUse"]
                );
              }
            });
            throw response;
          }
        });
    } else {
      return Promise.resolve(this.props.onSubmitSucceeded(payload, form));
    }
  }
}
